import React, { useCallback, useMemo } from 'react';

import { isTemplateOrganizationItem } from '@shared/helpers/organization.helpers';
import { getSbefCode } from '@shared/helpers/sbef.helpers';
import { ElementPropertyName } from '@shared/models/element-property.model';
import { IElement } from '@shared/models/project.model';
import { Recipe } from '@shared/models/recipe.model';

import { EmptyStateProps } from '@ui/EmptyState';
import SelectList from '@ui/SelectList';
import type { SelectListChildren } from '@ui/menus/menu.model';

import { useObjectMemo } from '@client/hooks/use-object.hooks';
import { useUser } from '@client/hooks/use-user.hooks';
import { useLastSelectedRecipe } from '@client/store/recipe/recipe.hook';

import RecipeListItem from './RecipeListItem';
import { RecipeSelectorTab } from './RecipeSelectorTabs';
import { SbefCode } from '@shared/construction/sbef.model';

interface RecipeSelectorListProps {
  element: IElement;
  searchString: string;

  /** The selected sbef code. Undefined if none is selected. */
  sbefCode: SbefCode | undefined;
  selectedTab: RecipeSelectorTab | undefined;
  onClose: () => void;
}

const RecipeSelectorList: React.FC<RecipeSelectorListProps> = ({
  element,
  searchString,
  sbefCode,
  selectedTab,
  onClose,
}) => {
  const user = useUser();
  const { recipes: lastSelectedRecipes } = useLastSelectedRecipe();

  const filteredRecipes = useMemo(() => {
    const filteredBySBEF = lastSelectedRecipes.filter(
      getSbefRecipeFilter(sbefCode),
    );

    switch (selectedTab) {
      case 'owner':
        return filteredBySBEF.filter((recipe) => recipe.owner === user?.id);
      case 'team':
        return filteredBySBEF.filter(
          (recipe) => !isTemplateOrganizationItem(recipe),
        );
      case 'public':
        return filteredBySBEF.filter(isTemplateOrganizationItem);
      default:
        return filteredBySBEF;
    }
  }, [lastSelectedRecipes, sbefCode, selectedTab, user?.id]);

  const emptyStateProps = useObjectMemo<EmptyStateProps>({
    message: 'No recipes found',
    fullHeight: true,
  });

  const recipeRenderer = useCallback<SelectListChildren<Recipe>>(
    (item, style) => (
      <RecipeListItem
        style={style}
        recipe={item}
        element={element}
        onSelect={onClose}
      />
    ),
    [element, onClose],
  );

  return (
    <SelectList
      enableVirtualization
      searchString={searchString}
      items={filteredRecipes}
      emptyStateProps={emptyStateProps}
    >
      {recipeRenderer}
    </SelectList>
  );
};

/**
 * Filter recipes by SBEF label
 * @param label - The SBEF label ("[code]. [description]") to filter by
 * @returns A function that filters recipes by SBEF label
 */
const getSbefRecipeFilter =
  (label: SbefCode | undefined) =>
  (recipe: Recipe): boolean => {
    // If no SBEF label is provided, show all recipes
    if (!label) return true;

    // Get the code part from the SBEF label
    const code = getSbefCode(label);

    // Get the SBEF label (e.g. "[code]. [description]") from the recipe
    const recipeLabel =
      recipe.category_property_value_record[ElementPropertyName.SbefCode];

    // If the recipe SBEF label is not a string, skip the recipe
    if (typeof recipeLabel !== 'string') return false;

    // Get the code part from the recipe SBEF label
    const recipeCode = getSbefCode(recipeLabel);

    // Codes between 101-105 do not belong to any group and should only be shown if it's an exact match with selected SBEF
    if (Number(code) > 100) return recipeCode === code;

    // For recipes with codes between 0-99, show recipes that belongs to the same group as the selected SBEF
    // or if the recipe code is an exact match with the selected SBEF
    return (
      Number(recipeCode) < 100 &&
      !!code &&
      !!recipeCode &&
      recipeCode.startsWith(code)
    );
  };

export default RecipeSelectorList;
